import { projectSchema, type Project } from '@finsim/model';
import { createEffect, createEvent, createStore, merge, sample } from 'effector';
import type { z } from 'zod';
import { applyCommand, CommandError, type Command } from './lib/commands';

/*
 * $project is the single source of truth for the edited project (CLAUDE.md). Edits arrive as
 * commands; each result is validated before it is committed, so the store only ever holds valid
 * projects. Undo/redo keeps snapshots (structurally shared, so cheap).
 */

export const HISTORY_LIMIT = 200;
/** Edits with the same coalesce key within this window merge into one undo step (typing). */
export const COALESCE_MS = 1000;

export interface CommandInput {
  command: Command;
  /** e.g. `${itemId}:amount` — consecutive edits of one field become a single undo step. */
  coalesceKey?: string;
}

interface Stamped extends CommandInput {
  at: number;
}

interface HistoryState {
  project: Project | null;
  past: Project[];
  future: Project[];
  lastKey: string | null;
  lastAt: number;
}

const EMPTY: HistoryState = { project: null, past: [], future: [], lastKey: null, lastAt: 0 };

/** An editable project was opened (from the library). Autosave and sync follow these. */
export const projectLoaded = createEvent<Project>();
/**
 * A project opened from a share link: shown read-only, never saved or synced. It deliberately
 * doesn't fire `projectLoaded`, so autosave, sync and onboarding never see it.
 */
export const sharedProjectOpened = createEvent<Project>();
/** Any project is now shown, editable or not (view state resets on this). */
export const projectShown = merge([projectLoaded, sharedProjectOpened]);
export const projectClosed = createEvent();
export const commandIssued = createEvent<CommandInput>();
export const undo = createEvent();
export const redo = createEvent();

/** Fired after a command changed the project (not for undo/redo or loads). */
export const editCommitted = createEvent<{ command: Command; project: Project }>();
export const commandRejected = createEvent<{
  command: Command;
  reason: string;
  issues: z.core.$ZodIssue[];
}>();

type Outcome =
  | { ok: true; project: Project; input: Stamped }
  | { ok: false; input: Stamped; reason: string; issues: z.core.$ZodIssue[] };

/** Stamps time outside the reducer (keeps reducers pure) and applies + validates the command. */
export const applyCommandFx = createEffect(
  ({ project, input }: { project: Project | null; input: CommandInput }): Outcome => {
    const stamped: Stamped = { ...input, at: performance.now() };
    if (!project) return { ok: false, input: stamped, reason: 'noProject', issues: [] };
    try {
      const next = applyCommand(project, input.command);
      const parsed = projectSchema.safeParse(next);
      if (!parsed.success) {
        return { ok: false, input: stamped, reason: 'invalid', issues: parsed.error.issues };
      }
      // Keep our structurally shared object (parse output is an equal deep copy).
      return { ok: true, project: next, input: stamped };
    } catch (error) {
      if (error instanceof CommandError)
        return { ok: false, input: stamped, reason: error.code, issues: [] };
      throw error;
    }
  },
);

/** The open project can't be edited: commands are ignored before they reach it. */
export const $readOnly = createStore(false)
  .on(sharedProjectOpened, () => true)
  .reset(projectLoaded, projectClosed);

const $history = createStore<HistoryState>(EMPTY)
  .on(projectShown, (_, project) => ({ ...EMPTY, project }))
  .on(projectClosed, () => EMPTY)
  .on(applyCommandFx.doneData, (state, outcome) => {
    if (!outcome.ok || !state.project || outcome.project === state.project) return state;
    const { coalesceKey, at } = outcome.input;
    const coalesce =
      coalesceKey !== undefined && coalesceKey === state.lastKey && at - state.lastAt < COALESCE_MS;
    return {
      project: outcome.project,
      past: coalesce ? state.past : [...state.past, state.project].slice(-HISTORY_LIMIT),
      future: [],
      lastKey: coalesceKey ?? null,
      lastAt: at,
    };
  })
  .on(undo, (state) => {
    const previous = state.past.at(-1);
    if (!previous || !state.project) return state;
    return {
      project: previous,
      past: state.past.slice(0, -1),
      future: [state.project, ...state.future],
      lastKey: null,
      lastAt: 0,
    };
  })
  .on(redo, (state) => {
    const next = state.future[0];
    if (!next || !state.project) return state;
    return {
      project: next,
      past: [...state.past, state.project],
      future: state.future.slice(1),
      lastKey: null,
      lastAt: 0,
    };
  });

export const $project = $history.map((s) => s.project);
const NO_KEY_DATES: Project['keyDates'] = [];
/** The open project's key dates; unchanged (no update) while edits leave them alone. */
export const $keyDates = $project.map((p) => p?.keyDates ?? NO_KEY_DATES);
export const $canUndo = $history.map((s) => s.past.length > 0);
export const $canRedo = $history.map((s) => s.future.length > 0);

// Read-only projects have no history either, so undo and redo have nothing to do.
sample({
  clock: commandIssued,
  source: { project: $project, readOnly: $readOnly },
  filter: ({ readOnly }) => !readOnly,
  fn: ({ project }, input) => ({ project, input }),
  target: applyCommandFx,
});

sample({
  clock: applyCommandFx.doneData.filterMap((o) =>
    o.ok ? { command: o.input.command, project: o.project } : undefined,
  ),
  target: editCommitted,
});

sample({
  clock: applyCommandFx.doneData.filterMap((o) =>
    o.ok ? undefined : { command: o.input.command, reason: o.reason, issues: o.issues },
  ),
  target: commandRejected,
});
